/*
 * The Mako page's Settings section (sections/mako-settings.liquid): Mako's
 * Settings window next to the Mako panel, and every control in the window
 * changes the panel. Show or hide buttons, colored buttons (Tinted or
 * Solid), button spacing, columns, Fill the panel and Button confirmation;
 * the Shortcuts tab takes real keys. When it first comes into view it
 * changes a few settings by itself, until it's touched.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const WIN = { '⌘': 'Ctrl', '⌃': 'Ctrl', '⌥': 'Alt', '⇧': 'Shift' };
  const forOs = (text) => (isMac ? text : text.replace(/[⌘⌃⌥⇧]/g, (c) => WIN[c]));
  const MODS = ['Shift', 'Control', 'Alt', 'Meta'];
  // Tinted is a see-through wash of the label color; Solid is nearly opaque.
  const ALPHA = { tint: 0.24, solid: 0.92 };
  // Button size and gap per Button spacing, in px (Mako's SPACING).
  const SPACING = { compact: [30, 2], standard: [32, 4], roomy: [34, 8] };
  const PAD = 8;
  const DEFAULTS = { colors: false, style: 'tint', spacing: 'standard', columns: 'auto', fill: false, confirm: false, qc: true };

  const rgba = (hex, a) => {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
  };

  // A shortcut as Mako's field shows it: ⌃⌥⇧⌘K on a Mac, Ctrl+Alt+Shift+K elsewhere.
  const keyLabel = (e) => {
    let key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
    if (e.code && /^Key[A-Z]$/.test(e.code)) key = e.code.slice(3);
    else if (e.code && /^Digit\d$/.test(e.code)) key = e.code.slice(5);
    if (key === ' ') key = 'Space';
    if (isMac) return (e.ctrlKey ? '⌃' : '') + (e.altKey ? '⌥' : '') + (e.shiftKey ? '⇧' : '') + (e.metaKey ? '⌘' : '') + key;
    return [e.ctrlKey || e.metaKey ? 'Ctrl' : '', e.altKey ? 'Alt' : '', e.shiftKey ? 'Shift' : '', key].filter(Boolean).join('+');
  };

  const init = (root) => {
    if (root.dataset.mzReady) return;
    root.dataset.mzReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => [...root.querySelectorAll(sel)];
    const panel = q('[data-mz-tools]');
    const win = q('[data-mz-win]');
    const sheet = q('[data-mz-sheet]');
    if (!panel || !win) return;
    const tools = qa('.mz-tool[data-color]');
    const empty = root.dataset.empty || 'Add Shortcut';
    const recordingText = root.dataset.recording || 'Type keys…';

    qa('[data-mz-os]').forEach((el) => (el.textContent = forOs(el.textContent)));

    const state = { ...DEFAULTS };
    let touched = false;
    let recording = null;

    const tab = (name) => {
      qa('[data-mz-tab]').forEach((t) => t.setAttribute('aria-selected', String(t.dataset.mzTab === name)));
      qa('[data-mz-page]').forEach((p) => (p.hidden = p.dataset.mzPage !== name));
      if (sheet) sheet.scrollTop = 0;
    };

    const setSwitch = (el, on) => el && el.setAttribute('aria-checked', String(on));
    const setChoice = (name, value) =>
      qa(`[data-mz-choice="${name}"] [data-value]`).forEach((b) => b.setAttribute('aria-checked', String(b.dataset.value === value)));

    // Fill the panel, as Mako works it out: the biggest square buttons that
    // fit the panel, over the column count that makes them biggest (or the
    // Columns setting), centred.
    const fillSize = (count, cols, gap, w, h) => {
      const rows = Math.ceil(count / cols);
      const size = Math.min((h - 2 * PAD - (rows - 1) * gap) / rows, (w - 2 * PAD - (cols - 1) * gap) / cols);
      return Math.floor(Math.max(24, Math.min(160, size)));
    };
    const layout = () => {
      const [size, gap] = SPACING[state.spacing] || SPACING.standard;
      const forced = state.columns === 'auto' ? 0 : Number(state.columns);
      const count = panel.querySelectorAll('.mz-tool:not([hidden])').length;
      let cols = forced || 4;
      let px = size;
      if (state.fill && count) {
        const w = panel.clientWidth;
        const h = panel.clientHeight;
        if (forced) px = fillSize(count, forced, gap, w, h);
        else {
          px = 0;
          for (let c = 1; c <= count; c++) {
            const s = fillSize(count, c, gap, w, h);
            if (s > px) {
              px = s;
              cols = c;
            }
          }
        }
      }
      panel.style.setProperty('--size', `${px}px`);
      panel.style.setProperty('--gap', `${gap}px`);
      panel.style.setProperty('--icon', `${Math.round(Math.max(18, Math.min(64, px * 0.5)))}px`);
      panel.style.setProperty('--cols', cols);
      panel.classList.toggle('is-grid', Boolean(forced) || state.fill);
      panel.classList.toggle('is-fill', state.fill);
    };

    const apply = () => {
      panel.dataset.spacing = state.spacing;
      layout();
      panel.classList.toggle('is-colored', state.colors);
      panel.classList.toggle('is-solid', state.colors && state.style === 'solid');
      win.classList.toggle('is-colored', state.colors);
      tools.forEach((t) => t.style.setProperty('--tint', rgba(t.dataset.color, ALPHA[state.style])));
      const sub = q('[data-mz-colorstyle]');
      if (sub) sub.hidden = !state.colors;
      ['colors', 'fill', 'confirm', 'qc'].forEach((k) => setSwitch(q(`[data-mz-opt="${k}"]`), state[k]));
      ['style', 'spacing', 'columns'].forEach((k) => setChoice(k, String(state[k])));
    };

    const showButton = (tool, on) => {
      const btn = q(`.mz-tool[data-mz-tool="${tool}"]`);
      if (btn) btn.hidden = !on;
      layout();
      setSwitch(q(`[data-mz-show="${tool}"]`), on);
      const row = q(`[data-mz-row="${tool}"]`);
      if (row) row.classList.toggle('is-off', !on);
    };

    const setKeys = (field, text) => {
      field.textContent = text || empty;
      field.classList.toggle('is-empty', !text);
      field.dataset.keys = text || '';
    };

    const stopRecording = (keep) => {
      if (!recording) return;
      recording.classList.remove('is-recording');
      if (!keep) setKeys(recording, recording.dataset.keys);
      recording = null;
    };

    const reset = () => {
      Object.assign(state, DEFAULTS);
      qa('[data-mz-show]').forEach((s) => showButton(s.dataset.mzShow, true));
      stopRecording(false);
      qa('[data-mz-keys]').forEach((f) => setKeys(f, ''));
      apply();
    };

    // Button confirmation: green when the action works, as in Mako.
    const flash = (btn) => {
      if (!state.confirm || !btn.animate) return;
      btn.animate(
        [{ backgroundColor: 'rgba(52, 199, 89, 0.6)', color: '#fff' }, { backgroundColor: 'rgba(52, 199, 89, 0)' }],
        { duration: 900, easing: 'ease-out' }
      );
    };

    // Anything a visitor does ends the self-running demo.
    const touch = () => {
      touched = true;
    };
    root.addEventListener('pointerdown', touch);
    root.addEventListener('keydown', touch);

    root.addEventListener('click', (e) => {
      const t = e.target.closest('button, a');
      if (!t || !root.contains(t)) return;
      if (t.dataset.mzTab) tab(t.dataset.mzTab);
      else if (t.dataset.mzShow) {
        showButton(t.dataset.mzShow, t.getAttribute('aria-checked') !== 'true');
      } else if (t.dataset.mzOpt) {
        state[t.dataset.mzOpt] = !state[t.dataset.mzOpt];
        apply();
      } else if (t.dataset.value && t.parentElement.dataset.mzChoice) {
        const name = t.parentElement.dataset.mzChoice;
        state[name] = t.dataset.value;
        apply();
      } else if (t.hasAttribute('data-mz-keys')) {
        if (recording === t) return stopRecording(false);
        stopRecording(false);
        recording = t;
        t.classList.add('is-recording');
        t.classList.remove('is-empty');
        t.textContent = recordingText;
      } else if (t.hasAttribute('data-mz-reset')) reset();
      else if (t.hasAttribute('data-mz-done')) {
        stopRecording(false);
        tab('buttons');
      } else if (t.dataset.mzTool === 'settings') {
        win.animate &&
          win.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.015)' }, { transform: 'scale(1)' }], { duration: 360, easing: 'ease-out' });
      } else if (t.dataset.mzTool) flash(t);
    });

    // Recording a shortcut: a modifier and a key, or an F key on its own.
    document.addEventListener('keydown', (e) => {
      if (!recording) return;
      if (MODS.includes(e.key)) return;
      e.preventDefault();
      if (e.key === 'Escape') return stopRecording(false);
      if ((e.key === 'Backspace' || e.key === 'Delete') && !(e.ctrlKey || e.metaKey || e.altKey || e.shiftKey)) {
        recording.dataset.keys = '';
        return stopRecording(false);
      }
      const fKey = /^F\d{1,2}$/.test(e.key);
      if (!fKey && !(e.ctrlKey || e.metaKey || e.altKey || e.shiftKey)) return;
      const field = recording;
      stopRecording(true);
      setKeys(field, keyLabel(e));
    });
    document.addEventListener('pointerdown', (e) => {
      if (recording && !recording.contains(e.target)) stopRecording(false);
    });

    apply();
    if ('ResizeObserver' in window) new ResizeObserver(layout).observe(panel);

    // The self-running demo: a few settings change by themselves, as if a
    // hand were on the window, then it stays as it ends.
    if (still.matches || !('IntersectionObserver' in window)) return;
    const steps = [
      () => tab('buttons'),
      () => {
        state.colors = true;
        apply();
      },
      () => {
        state.style = 'solid';
        apply();
      },
      () => showButton('grid', false),
      () => tab('options'),
      () => {
        state.columns = '3';
        apply();
      },
      () => {
        state.fill = true;
        apply();
      },
      () => {
        state.spacing = 'roomy';
        apply();
      },
      () => {
        state.style = 'tint';
        apply();
      },
    ];
    const play = () => {
      let i = 0;
      const next = () => {
        if (touched || i >= steps.length) return;
        steps[i++]();
        window.setTimeout(next, 1300);
      };
      window.setTimeout(next, 900);
    };
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        io.disconnect();
        if (!touched) play();
      },
      { threshold: 0.5 }
    );
    io.observe(win);
  };

  const boot = (scope = document) => scope.querySelectorAll('[data-mako-settings]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
